import { IDisplayedSliceSetting, IPathNodeSetting } from '../interfaces';
import {
  IChartDataFlow,
  IParsedFlow,
  IPathLink,
  IPathNode,
  IPathNodeDisplayProps,
  ISankeyLink,
  ISankeyNode,
} from './interfaces';
import { PathGraph } from './graphUtils/pathGraph';
import { sankeyChartColors } from '../utils/analysisColorConfig';
import { PathNode } from './graphUtils/pathNode';
import { isMergedNode } from './utils';

const getNodeId = (node: PathNode, sliceIndex: number) =>
  node.key + '-' + sliceIndex;

const getSliceNodeDisplayProps = (
  node: PathNode,
  incomingTotalPerSeq: number[],
): IPathNodeDisplayProps => {
  const isFirstNodeSeq = node.nodeSeq === 0;
  // first sequence is special: there are no incoming nodes. To avoid displaying 0s there,
  // we can use the outgoing instead
  const outgoingCount = node.sumOfOutgoingWeights();
  const nodeIncomingCount = isFirstNodeSeq
    ? outgoingCount
    : node.sumOfIncomingWeights();
  const nodeIncomingCountFromOther = isFirstNodeSeq
    ? 0
    : node.sumOfIncomingWeightsFromMerged();
  const nodeIncomingTotalPerSeq =
    (isFirstNodeSeq
      ? incomingTotalPerSeq[1]
      : incomingTotalPerSeq[node.nodeSeq]) ?? 0;
  return {
    incomingCount: nodeIncomingCount,
    incomingCountFromOther: nodeIncomingCountFromOther,
    incomingTotalPerSeq: nodeIncomingTotalPerSeq,
    mergedNodes: node.mergedNodes,
    outgoingCount,
    othersConfigAction: node.othersConfigAction,
    fixedValue: nodeIncomingCount + nodeIncomingCountFromOther,
  };
};

const initializeSteps = (
  flows: IChartDataFlow[],
  displayedSliceSettings: IDisplayedSliceSetting[],
  eventsPerStep: number,
  pathNodeSettings: IPathNodeSetting[],
) => {
  const visibleFlows: Array<{
    flow: IChartDataFlow;
    groupIndex: number;
  }> = [];

  // filter only visible flows from the breakdown selection
  flows.forEach((flow) => {
    const groupIndex = displayedSliceSettings.findIndex(
      (s) => s.key === flow.key,
    );
    if (groupIndex === -1) {
      return;
    }
    const slice = displayedSliceSettings[groupIndex];
    if (slice.isVisible) {
      visibleFlows.push({ flow, groupIndex });
    }
  });

  const parsedFlows: IParsedFlow[] = visibleFlows.map(
    ({ flow: { key, name, steps, userAlias, originalHint }, groupIndex }) => ({
      key,
      name,
      graph: new PathGraph(steps),
      groupIndex,
      userAlias,
      originalHint,
    }),
  );
  // ensures that nodes on the chart are drawn in the same order as groups
  // otherwise the tooltips of bottom part of the bars appear behind the
  // bars of the upper half
  parsedFlows.sort((a, b) => a.groupIndex - b.groupIndex);
  const pathGraphs = parsedFlows.map((flow) => flow.graph);
  const nodeScore = PathGraph.mergeNodesByScore(
    pathGraphs,
    eventsPerStep,
    pathNodeSettings,
  );

  return { parsedFlows, nodeScore };
};

export const transformData = (
  flows: IChartDataFlow[],
  displayedSliceSettings: IDisplayedSliceSetting[],
  displaySeq: number,
  eventsPerStep: number,
  pathNodeSettings: IPathNodeSetting[],
) => {
  const pathNodes: IPathNode[] = [];
  const pathLinks: IPathLink[] = [];
  let maxSeq = 0;

  const { parsedFlows, nodeScore } = initializeSteps(
    flows,
    displayedSliceSettings,
    eventsPerStep,
    pathNodeSettings,
  );

  parsedFlows.forEach((flow) => {
    const { groupIndex, graph } = flow;

    maxSeq = Math.max(maxSeq, graph.maxNodeSeq);
    const { nodesToDisplaySeq, incomingTotalPerSeq } =
      graph.getNodesUpToDisplaySeq(displaySeq);

    nodesToDisplaySeq.forEach((node) => {
      const pathColorIndex = node.nodeSeq % sankeyChartColors.length;
      const id = getNodeId(node, groupIndex);
      const displayProps = getSliceNodeDisplayProps(node, incomingTotalPerSeq);
      pathNodes.push({
        id,
        colorIndex: pathColorIndex,
        key: node.key,
        seq: node.nodeSeq,
        name: node.name,
        groupIndex,
        groupName: flow.name,
        groupAlias: flow.userAlias,
        groupHint: flow.originalHint,
        nodeScore: nodeScore[node.key] ?? 0,
        visible: true,
        textVisible: false,
        ...displayProps,
      });
    });

    nodesToDisplaySeq.forEach((sourceNode) => {
      const isLastSeq = sourceNode.nodeSeq === displaySeq;
      // do not create outgoing links for "Others" node and nodes that are at last sequence
      // (to cut off the Path chart)
      if (sourceNode.isMerged() || isLastSeq) {
        return;
      }
      const pathColorIndex = sourceNode.nodeSeq % sankeyChartColors.length;
      const grapPathLinks = sourceNode.outgoingAdjacencyList();
      grapPathLinks.forEach((link) => {
        const destNode = link.getDestNode();
        const sourceId = getNodeId(sourceNode, groupIndex);
        const targetId = getNodeId(destNode, groupIndex);
        pathLinks.push({
          colorIndex: pathColorIndex,
          sourceId,
          targetId,
          value: link.getWeight(),
          groupIndex,
          visible: true,
          isHighlight: false,
        });
      });
    });
  });

  return {
    pathNodes,
    pathLinks,
    maxSeq,
  };
};

const enableLargestTargetNextIndexNode = (
  nodes: ISankeyNode[],
  links: ISankeyLink[],
  nodeIndex: number,
) => {
  let targetNodeIndex: number = nodeIndex;
  nodes[targetNodeIndex].visible = true;
  nodes[targetNodeIndex].textVisible = true;
  while (targetNodeIndex >= 0) {
    const nextLinks = [];
    for (const link of links) {
      if (link.source.index === targetNodeIndex) {
        nextLinks.push(link);
      }
    }
    let largerLinkIndex = -1;
    let maxValue = -1;

    nextLinks.forEach((link, index: number) => {
      if (link.value > maxValue && !isMergedNode(link.target)) {
        maxValue = link.value;
        largerLinkIndex = index;
      }
    });

    if (!nextLinks || largerLinkIndex === -1) {
      break;
    }

    const resLinkIndex = nextLinks[largerLinkIndex].index;
    highlightLinkAndNodes(links[resLinkIndex]);
    targetNodeIndex = nextLinks[largerLinkIndex].target.index;
  }
};

const highlightLinkAndNodes = (link: ISankeyLink) => {
  link.visible = true;
  link.target.visible = true;
  link.target.textVisible = true;
  link.source.visible = true;
  link.source.textVisible = true;
};

const enableLargestSourceBeforeIndexNode = (
  nodes: ISankeyNode[],
  links: ISankeyLink[],
  nodeIndex: number,
) => {
  let sourceNodeIndex = nodeIndex;
  nodes[sourceNodeIndex].visible = true;
  nodes[sourceNodeIndex].textVisible = true;
  while (sourceNodeIndex >= 0) {
    const prevLinks = [];
    for (const link of links) {
      if (link.target.index === sourceNodeIndex) {
        prevLinks.push(link);
      }
    }
    let largerLinkIndex = -1;
    let maxValue = -1;

    prevLinks.forEach((link, index: number) => {
      if (link.value > maxValue) {
        maxValue = link.value;
        largerLinkIndex = index;
      }
    });

    if (!prevLinks || largerLinkIndex === -1) {
      break;
    }

    const resLinkIndex = prevLinks[largerLinkIndex].index;
    highlightLinkAndNodes(links[resLinkIndex]);
    sourceNodeIndex = prevLinks[largerLinkIndex].source.index;
  }
};

const enableNextLevelNodes = (links: ISankeyLink[], nodeIndex: number) => {
  for (const link of links) {
    if (link.source.index === nodeIndex) {
      link.target.visible = true;
      link.target.textVisible = true;
      link.visible = true;
    }
  }
};

const disableAllVisible = (nodes: ISankeyNode[], links: ISankeyLink[]) => {
  nodes.forEach((node) => {
    node.visible = false;
    node.textVisible = false;
  });
  links.forEach((link) => {
    link.visible = false;
    link.isHighlight = false;
  });
};

export function observeNode(
  nodes: ISankeyNode[],
  links: ISankeyLink[],
  nodeIndex: number,
) {
  disableAllVisible(nodes, links);
  enableLargestSourceBeforeIndexNode(nodes, links, nodeIndex);
  enableLargestTargetNextIndexNode(nodes, links, nodeIndex);
  enableNextLevelNodes(links, nodeIndex);
}

export const getLargestPrevStepEventsToStart = (
  currentStep: ISankeyNode,
): string[][] => {
  const stepsEvents: string[][] = [];
  getLargestPrevStepEventsToStartRecursive(currentStep, stepsEvents);
  // steps are collected backwards - the last index is the first step,
  // thus we need to reverse the list
  stepsEvents.reverse();
  return stepsEvents;
};

export const getLargestPrevStepEventsToStartRecursive = (
  currentStep: ISankeyNode,
  stepsEvents: string[][],
) => {
  if (currentStep.targetLinks.length > 0) {
    // the first link is the largest
    const prevStep = currentStep.targetLinks[0].source;
    stepsEvents.push([prevStep.name]);
    getLargestPrevStepEventsToStartRecursive(prevStep, stepsEvents);
  }
};
